import axios from "axios";
import React, { useEffect, useState } from "react";
import { Container, Nav, Navbar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SearchSidebar from "./SearchSidebar";
import Sidebar from "./Sidebar";
import UserMenu from "./UserMenu";
import { useCompanyFont } from "../hooks/useCompanyFont";

import "../styles/NavBar.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

export default function NavigationBar({ onSidebarStateChange }) {
  const navigate = useNavigate();
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyFontKey, setCompanyFontKey] = useState(null);
  const [companyFontSize, setCompanyFontSize] = useState("24");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load company font globally
  useCompanyFont(companyFontKey);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isHoveringCategory, setIsHoveringCategory] = useState(false);
  const [isHoveringSidebar, setIsHoveringSidebar] = useState(false);
  const [showsearchSidebar, setShowsearchSidebar] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const hoverTimeoutRef = React.useRef(null);
  const categoryHoverTimeoutRef = React.useRef(null);
  const profileFetchAttemptedRef = React.useRef(false);
  const categoriesContainerRef = React.useRef(null);

  const [country, setCountry] = useState(() => {
    return localStorage.getItem("selectedCountry") || "US";
  });

  // Get auth token from logged in user
  const getAuthToken = () => {
    const token = localStorage.getItem("authToken");
    // Return null if token is empty string or doesn't exist
    return token && token.trim() ? token : null;
  };

  // Fetch company logo and name
  useEffect(() => {
    if (!COMPANY_CODE) {
      console.error("Company code not configured");
      return;
    }

    // Fetch company logo
    axios
      .get(`${BASE_URL}/api/customer/company/get-company-logo`, {
        params: {
          company_code: COMPANY_CODE,
        },
      })
      .then((response) => {
        if (response.data.company_logo) {
          const logoUrl = `${BASE_URL}/uploads/company_logos/${response.data.company_logo}`;
          setCompanyLogo(logoUrl);
        }
      })
      .catch((error) => {
        console.error("Error fetching company logo:", error);
      });

    // Fetch company details
    axios
      .get(`${BASE_URL}/api/customer/company/get-company-details`, {
        params: {
          company_code: COMPANY_CODE,
        },
      })
      .then((response) => {
        if (response.data.company_name) {
          setCompanyName(response.data.company_name);
        }
        if (response.data.company_font_key) {
          setCompanyFontKey(response.data.company_font_key);
        }
        if (response.data.company_font_size) {
          setCompanyFontSize(response.data.company_font_size);
        }
      })
      .catch((error) => {
        console.error("Error fetching company details:", error);
      });
  }, []);

  // Fetch categories
  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/customer/main-categories`, {
        params: {
          company_code: COMPANY_CODE,
        },
      })
      .then((response) => {
        setCategories(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        setError("Failed to fetch categories");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let timeOutId;
    if (isHoveringCategory || isHoveringSidebar) {
      setSidebarOpen(true);
      // Clear any close timeout when hovering
      if (timeOutId) {
        clearTimeout(timeOutId);
      }
    } else {
      // Only close after delay when not hovering on either
      timeOutId = setTimeout(() => {
        setSidebarOpen(false);
        setSelectedCategory(null);
      }, 100);
    }
    return () => {
      if (timeOutId) {
        clearTimeout(timeOutId);
      }
    };
  }, [isHoveringCategory, isHoveringSidebar]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (categoryHoverTimeoutRef.current) {
        clearTimeout(categoryHoverTimeoutRef.current);
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Check if categories need scrolling
  useEffect(() => {
    const checkScroll = () => {
      if (categoriesContainerRef.current) {
        const container = categoriesContainerRef.current;
        setCanScrollLeft(container.scrollLeft > 0);
        setCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth - 10
        );
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);
    
    return () => window.removeEventListener("resize", checkScroll);
  }, [categories]);

  // Communicate sidebar state to parent App component
  useEffect(() => {
    if (onSidebarStateChange) {
      onSidebarStateChange(sidebarOpen);
    }

    const event = new CustomEvent("sidebarStateChange", {
      detail: { isOpen: sidebarOpen },
    });
    window.dispatchEvent(event);
  }, [sidebarOpen, onSidebarStateChange]);

  useEffect(() => {
    localStorage.setItem("selectedCountry", country);
  }, [country]);

  useEffect(() => {
    const token = getAuthToken();
    
    // Only fetch if we have a valid token and haven't already attempted
    if (!token || profileFetchAttemptedRef.current) {
      return;
    }

    profileFetchAttemptedRef.current = true;

    // Create abort controller to cancel request if component unmounts
    const abortController = new AbortController();

    axios
      .get(`${BASE_URL}/api/customer/user/profile`, {
        params: { company_code: COMPANY_CODE },
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController.signal,
      })
      .then((res) => {
        const data = res.data;
        setProfilePicture(
          data.profile_image
            ? `${BASE_URL}/uploads/profile_images/${data.profile_image}`
            : null
        );
      })
      .catch((err) => {
        // Only log unexpected errors
        if (err.code !== 'ERR_CANCELED' && err.response?.status !== 404) {
          console.error("Failed to load user profile:", err);
        }
      });

    // Cleanup function to abort request on unmount
    return () => abortController.abort();
  }, []);

  // Handlers for mouse enter and leave events
  const handleCategoryMouseEnter = (category) => {
    // Clear any existing timeout
    if (categoryHoverTimeoutRef.current) {
      clearTimeout(categoryHoverTimeoutRef.current);
    }

    // Set selected category immediately for visual feedback
    setSelectedCategory(category);

    // If sidebar is already open, immediately set hovering to true
    // Otherwise, add delay before opening sidebar
    if (sidebarOpen) {
      setIsHoveringCategory(true);
    } else {
      categoryHoverTimeoutRef.current = setTimeout(() => {
        setIsHoveringCategory(true);
      }, 100);
    }
  };

  const handleCategoryMouseLeave = () => {
    // Clear the timeout if mouse leaves before delay completes
    if (categoryHoverTimeoutRef.current) {
      clearTimeout(categoryHoverTimeoutRef.current);
      categoryHoverTimeoutRef.current = null;
    }
    // Only set to false, don't clear selected category yet
    setIsHoveringCategory(false);
  };

  // Handlers for sidebar mouse enter and leave events
  const handleSidebarMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Clear category timeout too if hovering over sidebar
    if (categoryHoverTimeoutRef.current) {
      clearTimeout(categoryHoverTimeoutRef.current);
    }
    setIsHoveringSidebar(true);
  };

  const handleSidebarMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHoveringSidebar(false);
  };

  // Scroll handlers for categories
  const handleScrollLeft = () => {
    if (categoriesContainerRef.current) {
      const scrollAmount = 200;
      categoriesContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: "smooth",
      });
      setTimeout(() => {
        if (categoriesContainerRef.current) {
          setCanScrollLeft(categoriesContainerRef.current.scrollLeft > 0);
          setCanScrollRight(
            categoriesContainerRef.current.scrollLeft <
              categoriesContainerRef.current.scrollWidth -
                categoriesContainerRef.current.clientWidth -
                10
          );
        }
      }, 300);
    }
  };

  const handleScrollRight = () => {
    if (categoriesContainerRef.current) {
      const scrollAmount = 200;
      categoriesContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
      setTimeout(() => {
        if (categoriesContainerRef.current) {
          setCanScrollLeft(categoriesContainerRef.current.scrollLeft > 0);
          setCanScrollRight(
            categoriesContainerRef.current.scrollLeft <
              categoriesContainerRef.current.scrollWidth -
                categoriesContainerRef.current.clientWidth -
                10
          );
        }
      }, 300);
    }
  };

  // Render the navigation bar
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    navigate(`/shop/${category.toLowerCase()}`);
    setSidebarOpen(false);
  };

  const handleHamburgerClicked = () => {
    setSidebarOpen(true);
  };

  const handleCategoryClickFromSidebar = (category) => {
    setSelectedCategory(category);
    navigate(`/shop/${category.toLowerCase()}`);
    setSidebarOpen(false);
  };

  // handle country change
  const handleCountryChange = (e) => {
    setCountry(e.target.value);
    window.location.reload();
  };

  // if loading or error state
  if (loading) {
    return <div>Loading categories...</div>;
  }
  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <Navbar bg="light" expand="lg" className="shadow-sm sticky-top nav-bar">
        <Container fluid className="navbar-container">
          {/* Row 1: Logo, Company Name, and Right Side Items */}
          <div className="navbar-row-1">
            {/* Left side - Logo */}
            <Navbar.Brand href="/" className="navbar-brand-mobile">
              <img src={companyLogo} alt="Logo" className="navbar-logo" />
            </Navbar.Brand>

            {/* Company Name - Center */}
            <div className="company-name-center">
              <h5 className="company-name-text" style={{ fontFamily: "var(--company-font)", fontSize: `${companyFontSize}px` }}>
                {companyName}
              </h5>
            </div>

            {/* Right side */}
            <div className="d-flex align-items-center right-navbar">
              {/* Country Select */}
              <select
                className="form-select country-select"
                aria-label="Select country"
                value={country}
                onChange={handleCountryChange}
              >
                <option value="UK">UK</option>
                <option value="US">US</option>
                <option value="SL">SL</option>
              </select>

              {/* Search icon */}
              <i
                className="bi bi-search search-icon"
                onClick={() => setShowsearchSidebar(true)}
              />

              {/* Cart icon */}
              <i
                className="bi bi-cart3 cart-icon"
                onClick={() => navigate("/cart")}
              />

              {/* User icons */}
              <UserMenu className="user-menu" profilePicture={profilePicture} />

              {/* Mobile hamburger menu */}
              <i
                className="bi bi-list d-block d-lg-none hamburger-icon"
                onClick={handleHamburgerClicked}
              />
            </div>
          </div>

          {/* Row 2: Categories */}
          <div className="navbar-row-2">
            {/* Left Arrow */}
            {canScrollLeft && (
              <button
                className="category-scroll-btn category-scroll-btn-left"
                onClick={handleScrollLeft}
                aria-label="Scroll categories left"
              >
                <i className="bi bi-chevron-left" />
              </button>
            )}

            {/* Categories Container */}
            <div
              className="categories-container"
              ref={categoriesContainerRef}
              onScroll={(e) => {
                const target = e.target;
                setCanScrollLeft(target.scrollLeft > 0);
                setCanScrollRight(
                  target.scrollLeft < target.scrollWidth - target.clientWidth - 10
                );
              }}
            >
              {/* Categories - Desktop */}
              <div className="d-none d-lg-flex categories-desktop">
                <Nav className="align-items-center flex-row">
                  {categories.map((category) => (
                    <Nav.Link
                      key={category.category_id}
                      role="button"
                      onMouseEnter={() =>
                        handleCategoryMouseEnter(category.category_name)
                      }
                      onMouseLeave={handleCategoryMouseLeave}
                      onClick={() => handleCategoryClick(category.category_name)}
                      className="navbar-category-link"
                    >
                      {category.category_name}
                    </Nav.Link>
                  ))}
                </Nav>
              </div>

              {/* Categories - Tablet */}
              <div className="d-none d-md-flex d-lg-none categories-tablet">
                <Nav className="align-items-center flex-row categories-scroll">
                  {categories.map((category) => (
                    <Nav.Link
                      key={category.category_id}
                      role="button"
                      onMouseEnter={() =>
                        handleCategoryMouseEnter(category.category_name)
                      }
                      onMouseLeave={handleCategoryMouseLeave}
                      onClick={() => handleCategoryClick(category.category_name)}
                      className="navbar-category-link"
                    >
                      {category.category_name}
                    </Nav.Link>
                  ))}
                </Nav>
              </div>
            </div>

            {/* Right Arrow */}
            {canScrollRight && (
              <button
                className="category-scroll-btn category-scroll-btn-right"
                onClick={handleScrollRight}
                aria-label="Scroll categories right"
              >
                <i className="bi bi-chevron-right" />
              </button>
            )}
          </div>
        </Container>
      </Navbar>

      {/* Sidebar */}
      <Sidebar
        category={selectedCategory}
        isOpen={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false);
          setSelectedCategory(null);
        }}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        categories={categories}
        onCategoryClick={handleCategoryClickFromSidebar}
      />

      {/* Search Sidebar */}
      <SearchSidebar
        show={showsearchSidebar}
        onClose={() => setShowsearchSidebar(false)}
      />
    </>
  );
}
